import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const listPublicEvents = createServerFn({ method: "GET" }).handler(async () => {
  const { fetchUpcomingEvents } = await import("@/lib/events.server");
  return fetchUpcomingEvents();
});

export const getPublicEvent = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const { fetchEventBySlug } = await import("@/lib/events.server");
    return fetchEventBySlug(String(data.slug).slice(0, 120));
  });

export const claimSlotPublic = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      slot_id: string;
      full_name: string;
      email: string;
      phone?: string;
      quantity?: number;
      note?: string;
      origin?: string;
      t1d_connection?: string;
      referral_source?: string;
      referral_name?: string;
      claim_answers?: Record<string, unknown>;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { claimSlotAsGuest } = await import("@/lib/events.server");
    return claimSlotAsGuest(data);
  });

export const claimSlotAsVolunteer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      slot_id: string;
      quantity?: number;
      note?: string;
      origin?: string;
      t1d_connection?: string;
      referral_source?: string;
      referral_name?: string;
      claim_answers?: Record<string, unknown>;
    }) => data,
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const email = (context.claims as { email?: string } | null)?.email ?? "";
    const profile = await supabase
      .from("volunteer_profiles")
      .select("full_name, phone, t1d_connection, shirt_size")
      .eq("user_id", userId)
      .maybeSingle();

    const { normalizeT1dConnection, normalizeReferralSource, claimConfigForSlot } = await import(
      "@/lib/events.server"
    );
    const { sanitizeClaimAnswers } = await import("@/lib/claim-fields");
    const { toProperCase } = await import("@/lib/proper-case");
    const answers = sanitizeClaimAnswers(await claimConfigForSlot(data.slot_id), data.claim_answers);
    const t1d =
      normalizeT1dConnection(data.t1d_connection) ??
      normalizeT1dConnection(profile.data?.t1d_connection);
    if (!t1d) throw new Error("Please choose your primary relationship to Type 1 Diabetes.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signupId, error } = await supabaseAdmin.rpc("claim_event_slot", {
      _slot_id: data.slot_id,
      _volunteer_id: userId,
      _full_name: toProperCase(
        profile.data?.full_name || email.split("@")[0] || "Volunteer",
      ).slice(0, 120),
      _email: email,
      _phone: profile.data?.phone ?? undefined,
      _quantity: Math.max(1, Math.min(Number(data.quantity ?? 1) || 1, 100)),
      _note: data.note?.trim().slice(0, 1000) || undefined,
      _t1d_connection: t1d,
      _referral_source: normalizeReferralSource(data.referral_source) ?? undefined,
      _referral_name: data.referral_name?.trim().slice(0, 160) || undefined,
      _claim_answers: answers,
    });
    if (error) throw new Error(error.message);

    // Keep the profile's shirt size current so the next claim form is prefilled.
    const { SHIRT_SIZE_OPTIONS } = await import("@/lib/claim-fields");
    const shirt = String(answers.shirt_size ?? "").trim();
    if (SHIRT_SIZE_OPTIONS.includes(shirt) && shirt !== profile.data?.shirt_size) {
      await supabaseAdmin
        .from("volunteer_profiles")
        .update({ shirt_size: shirt })
        .eq("user_id", userId);
    }

    const { sendSignupConfirmation, notifyAdminsOfSignup, resolveOrigin } = await import(
      "@/lib/events.server"
    );
    const origin = resolveOrigin(data.origin);
    await sendSignupConfirmation(String(signupId), email, null, origin);
    await notifyAdminsOfSignup(String(signupId), origin);
    return { ok: true as const, signupId: String(signupId) };

  });


export const listMyShifts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("event_signups")
      .select(
        "id, quantity, note, status, created_at, event_slots(id, title, slot_type, start_time, end_time, event_dates(event_date, events(id, title, slug, location_name, address)))",
      )
      .eq("volunteer_id", context.userId)
      .eq("status", "confirmed")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const cancelMyShift = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("event_signups")
      .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("volunteer_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
